/**
 * „Skieruj do akceptacji" — zbiorcze wysłanie zaznaczonych dokumentów Symfonii w obieg.
 *
 * Trzy rzeczy, które decydują o kształcie tego komponentu:
 *
 *  1. **Rezerwacja identyfikatorów idzie JEDNYM żądaniem.** Osobne per dokument wyczerpałoby
 *     limit sesji WebAPI (domyślnie 10) już przy kilkunastu zaznaczonych wierszach.
 *  2. **Starty obiegów idą po kolei, każdy osobno** — trasa startu jest z natury per rekord
 *     (pilnuje slotu, puli akceptantów, pól wymaganych). To wyłącznie NASZA baza, więc jest
 *     szybkie; równoległość kupiłaby niewiele, a utrudniłaby przypisanie błędu do dokumentu.
 *  3. **Wynik jest CZĘŚCIOWY i tak się go pokazuje.** Dokument z aktywnym obiegiem odbije się
 *     o strażnik slotu, korekta może nie mieć szczegółu w Symfonii. Przerywanie całej paczki
 *     przy pierwszym takim wierszu byłoby złe — użytkownik zaznaczył pięćdziesiąt świadomie.
 *     Dlatego zamiast jednego „udało się / nie udało" pokazujemy zestawienie per dokument.
 *
 * Ograniczenie powiedziane wprost: proces wymagający WSKAZANIA adresata na starcie nie nadaje
 * się do trybu zbiorczego (adresata trzeba wybrać per wniosek). Taki dokument wraca z czytelnym
 * powodem i odsyłamy do wysłania z formatki — zamiast po cichu go pomijać.
 */

import type { ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DECISION_PRIORITIES,
  type DecisionPriority,
  DEFAULT_DECISION_PRIORITY,
  HANDEL_TARGET_MODULE_CODE,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SendHorizonal } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { ToolbarButton } from '@/components/handel-magazyn/HmListShell';
import { Modal } from '@/components/Modal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  type HmLinkKind,
  reserveDocumentLinksBatch,
} from '@/lib/handel-magazyn/document-links-api';
import { fetchProcessesForModule, startProcess } from '@/lib/processes/instance-api';

import { approvalStatusesKey } from './useApprovalColumn';

/** Wynik per dokument — to on ląduje na ekranie po zakończeniu paczki. */
interface RowOutcome {
  label: string;
  ok: boolean;
  reason?: string;
}

export interface HmBulkApprovalButtonProps<T> {
  kind: HmLinkKind;
  companyId: string | null;
  selected: T[];
  /** `Id` dokumentu w Symfonii. */
  getExternalId: (row: T) => number;
  /** Numer dokumentu — wyłącznie do pokazania wyniku; użytkownik nie myśli identyfikatorami. */
  getLabel: (row: T) => string;
}

export function HmBulkApprovalButton<T>({
  kind,
  companyId,
  selected,
  getExternalId,
  getLabel,
}: HmBulkApprovalButtonProps<T>): ReactElement | null {
  const { t } = useTranslation();
  const { show } = useToast();
  const { me } = useTenantAuth();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [processId, setProcessId] = useState('');
  const [priority, setPriority] = useState<DecisionPriority>(DEFAULT_DECISION_PRIORITY);
  const [outcomes, setOutcomes] = useState<RowOutcome[] | null>(null);

  const processesQuery = useQuery({
    queryKey: ['processes', 'for-module', HANDEL_TARGET_MODULE_CODE],
    queryFn: () => fetchProcessesForModule(HANDEL_TARGET_MODULE_CODE),
    retry: false,
  });
  const processes = processesQuery.data ?? [];

  const run = useMutation({
    mutationFn: async (): Promise<RowOutcome[]> => {
      const byExternalId = new Map(selected.map((row) => [String(getExternalId(row)), row]));

      // 1. Rezerwacja identyfikatorów — jedno żądanie, jedna sesja Symfonii.
      const reserved = await reserveDocumentLinksBatch({
        kind,
        externalIds: [...byExternalId.keys()],
        companyId: companyId!,
      });

      // 2. Starty obiegów — po kolei, żeby błąd dało się przypisać do konkretnego dokumentu.
      const results: RowOutcome[] = [];
      for (const entry of reserved) {
        const row = byExternalId.get(entry.externalId);
        const label = row ? getLabel(row) : (entry.documentNumber ?? entry.externalId);

        if (!entry.linkId) {
          results.push({
            label,
            ok: false,
            reason: t('tenant.handelMagazyn.bulkApproval.noDocument'),
          });
          continue;
        }
        try {
          await startProcess(processId, { targetRecordId: entry.linkId, priority });
          results.push({ label, ok: true });
        } catch (err) {
          results.push({ label, ok: false, reason: startFailureReason(err, (k) => t(k)) });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      setOutcomes(results);
      // Kolumna „Akceptacja" i banery na formatkach mają pokazać nowy stan bez przeładowania.
      void queryClient.invalidateQueries({ queryKey: approvalStatusesKey(kind, companyId) });
      void queryClient.invalidateQueries({ queryKey: ['processes'] });
    },
    onError: () => {
      show({
        variant: 'error',
        title: t('tenant.handelMagazyn.bulkApproval.failed', 'Nie udało się skierować dokumentów'),
      });
    },
  });

  // Widoczny tylko wtedy, gdy jest czym kierować: uprawnienie do startu + opublikowany proces.
  const canCreate =
    !!me && (me.user.isSuperUser || me.permissions.includes('processes.instances.create'));
  if (!canCreate || processes.length === 0 || !companyId) return null;

  const openModal = () => {
    setProcessId(processes[0]?.id ?? '');
    setPriority(DEFAULT_DECISION_PRIORITY);
    setOutcomes(null);
    setOpen(true);
  };

  const okCount = outcomes?.filter((o) => o.ok).length ?? 0;

  return (
    <>
      <ToolbarButton
        icon={<SendHorizonal className="h-4 w-4" />}
        label={t('tenant.handelMagazyn.bulkApproval.button', 'Skieruj do akceptacji')}
        enabled={selected.length > 0}
        onClick={openModal}
        testId="hm-bulk-approval"
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('tenant.handelMagazyn.bulkApproval.title', 'Skieruj do akceptacji')}
        size="md"
        footer={
          outcomes ? (
            <Button
              variant="primary"
              size="sm"
              data-testid="hm-bulk-approval-close"
              onClick={() => setOpen(false)}
            >
              {t('common.close', 'Zamknij')}
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="hm-bulk-approval-cancel"
                onClick={() => setOpen(false)}
              >
                {t('common.cancel', 'Anuluj')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                data-testid="hm-bulk-approval-confirm"
                disabled={!processId}
                loading={run.isPending}
                onClick={() => run.mutate()}
              >
                {t('tenant.handelMagazyn.bulkApproval.confirm', 'Skieruj')}
              </Button>
            </>
          )
        }
      >
        {outcomes ? (
          <div data-testid="hm-bulk-approval-outcomes">
            <p className="mb-2 text-sm text-text-primary">
              {t('tenant.handelMagazyn.bulkApproval.summary', {
                ok: okCount,
                total: outcomes.length,
                defaultValue: 'Skierowano {{ok}} z {{total}} dokumentów.',
              })}
            </p>
            {/* Niepowodzenia wypisujemy per dokument — zbiorcze „część się nie udała" zmusiłoby
                do zgadywania, który wiersz poprawić. */}
            <ul className="max-h-64 space-y-1 overflow-y-auto text-sm">
              {outcomes.map((o) => (
                <li key={o.label} className="flex items-start gap-2">
                  <span className={o.ok ? 'text-emerald-600' : 'text-red-600'}>
                    {o.ok ? '✓' : '✕'}
                  </span>
                  <span className="font-mono text-xs text-text-primary">{o.label}</span>
                  {o.reason ? <span className="text-text-secondary">— {o.reason}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            <p className="mb-3 text-sm text-text-secondary">
              {t('tenant.handelMagazyn.bulkApproval.intro', {
                count: selected.length,
                defaultValue: 'Zaznaczone dokumenty: {{count}}.',
              })}
            </p>

            {processes.length > 1 ? (
              <label className="block">
                <span className="mb-1 block text-text-secondary">
                  {t('tenant.processes.picker.selectProcess', 'Proces')}
                </span>
                <Dropdown
                  data-testid="hm-bulk-approval-process"
                  value={processId}
                  onChange={setProcessId}
                  options={processes.map((p) => ({ value: p.id, label: p.name }))}
                />
              </label>
            ) : (
              <div data-testid="hm-bulk-approval-process-single" className="font-medium">
                {processes[0]?.name}
              </div>
            )}

            <label className="mt-3 block">
              <span className="mb-1 block text-text-secondary">
                {t('tenant.processes.picker.priority', 'Priorytet')}
              </span>
              <Dropdown
                data-testid="hm-bulk-approval-priority"
                value={priority}
                onChange={(value) => setPriority(value as DecisionPriority)}
                options={DECISION_PRIORITIES.map((p) => ({
                  value: p,
                  label: t(`tenant.processes.priorityLabel.${p}`),
                }))}
              />
            </label>

            <p className="mt-3 text-xs text-text-tertiary">
              {t('tenant.handelMagazyn.bulkApproval.note')}
            </p>
          </>
        )}
      </Modal>
    </>
  );
}

/**
 * Odmowa startu → czytelny powód przy KONKRETNYM dokumencie.
 *
 * Najczęstszy przypadek to zajęty slot (dokument już był kierowany) — i to nie jest awaria,
 * tylko normalny stan, który trzeba nazwać po imieniu, a nie pokazać jako „błąd serwera".
 */
function startFailureReason(err: unknown, tr: (key: string) => string): string {
  const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  if (code === 'RECORD_HAS_INSTANCE' || code === 'INSTANCE_ALREADY_ACTIVE') {
    return tr('tenant.handelMagazyn.bulkApproval.alreadyInFlow');
  }
  if (code === 'ADDRESSEE_REQUIRED') {
    return tr('tenant.handelMagazyn.bulkApproval.needsAddressee');
  }
  // Pierwszy etap procesu nie ma akceptantów — obieg na tym dokumencie nie ruszył.
  if (code === 'NO_APPROVERS') {
    return tr('tenant.handelMagazyn.bulkApproval.noApprovers');
  }
  // Błędnie narysowany start procesu (definicja zmieniona po publikacji) — obieg nie ruszył.
  if (code === 'INVALID_START_ROUTE') {
    return tr('tenant.handelMagazyn.bulkApproval.invalidStartRoute');
  }
  // Proces podpięty do wzoru centralnego, którego nie da się użyć (usunięty, niepublikowany albo
  // uszkodzony) — obieg na tym dokumencie nie ruszył; poprawić go może tylko administrator.
  if (code === 'TEMPLATE_UNAVAILABLE') {
    return tr('tenant.handelMagazyn.bulkApproval.templateUnavailable');
  }
  // Proces wycofany z publikacji albo wyłączony od chwili pobrania listy procesów — obieg na tym
  // dokumencie nie ruszył; wybrać inny proces (albo poprosić administratora) może sam użytkownik.
  if (code === 'PROCESS_NOT_PUBLISHED') {
    return tr('tenant.handelMagazyn.bulkApproval.processNotPublished');
  }
  if (code === 'PROCESS_INACTIVE') {
    return tr('tenant.handelMagazyn.bulkApproval.processInactive');
  }
  return tr('tenant.handelMagazyn.bulkApproval.startFailed');
}
